<template>
  <div class="row">
    <div class="text-center mw-100 px-5" style="margin: auto" v-if="isMobile">
      <i class="fa fa-mobile-phone fa-rotate-90 fa-3x"></i>
      <br />
      <p class="font-weight-500">
        This section is best viewed in landscape mode
      </p>
    </div>

    <div class="col-12">
      <p class="lead" style="letter-spacing: -0.5px">
        The city rankings table below summarizes the future readiness positions
        of the 102 cities in the CFRI 2025 Report. It lists cities in order of
        decreasing overall performance (column 1). Data inside the table
        (columns 2 onwards) refer to the scores and ranks in each of the CFRI
        pillars and sub-pillars.
      </p>
      <p>
        How to read this table: within each pillar, different shades correspond
        to different relative levels of performance: darker shades indicate
        areas in which the city performs relatively well, whereas the lightest
        shade points at areas requiring priority attention. Intermediate shading
        corresponds to areas for which improvement remains important.
      </p>
      <hr />
    </div>

    <!-- Score/Rank Toggle -->
    <div class="col-lg-6 col-12 mb-3 ranking-toggle">
      <b-form-group v-slot="{ ariaDescribedby }">
        <b-form-radio-group
          class="btn-group special"
          id="score-rank-toggle"
          v-model="mode"
          :options="modeOptions"
          :aria-describedby="ariaDescribedby"
          buttons
          button-variant="outline-green"
          name="score-rank-toggle"
        ></b-form-radio-group>
      </b-form-group>
    </div>

    <!-- Column Toggle Controls -->
    <div class="col-12 mb-4">
      <div class="card">
        <div class="card-body">
          <!-- Main Pillars -->
          <div class="row mb-3">
            <div class="col-12">
              <h6 class="text-primary mb-2">Main Pillars</h6>
              <div class="row">
                <div
                  class="col-lg-3 col-md-6 col-12"
                  v-for="pillar in mainPillars"
                  :key="'pillar-' + pillar.key"
                >
                  <b-form-checkbox v-model="pillar.visible" :id="pillar.key">
                    {{ pillar.label }}
                  </b-form-checkbox>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="text-center my-3" style="text-align: center; width: 100%">
      <p
        class="font-weight-500 help-text-green text-center"
        style="text-align: center"
      >
        Please scroll to the right to see additional pillars
      </p>
    </div>

    <!-- Rankings Table -->
    <div class="col-12" style="overflow: auto">
      <b-table
        id="city-rankings-table"
        class="rankingsTable"
        :items="sortedCities"
        :fields="visibleFields"
        small
        fixed
        primary-key="City"
        :tbody-transition-props="transProps"
        responsive
      >
        <!-- Rank Column -->
        <template #cell(Rank)="data">
          <div class="rank-cell">
            <span class="font-weight-bold">{{ data.index + 1 }}</span>
          </div>
        </template>

        <!-- City Column -->
        <template #cell(City)="data">
          <div class="city-cell">
            <span class="city-name" @click="goToProfile(data.item.Slug)">
              {{ data.value }}
            </span>
            <small class="text-muted d-block">{{ data.item.Country }}</small>
          </div>
        </template>

        <!-- Future Readiness (Overall) -->
        <template #cell(FutureReadiness)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'FutureReadiness')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'FutureReadiness'" class="font-weight-bold">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Dynamic Pillar/Sub-pillar Columns -->
        <!-- People Centricity -->
        <template #cell(PeopleCentricity)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'PeopleCentricity')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'PeopleCentricity'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Openness -->
        <template #cell(Openness)="data">
          <div class="py-3" :class="getBackgroundClass(data.value, 'Openness')">
            <transition name="fade" mode="out-in">
              <span :key="mode + 'Openness'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Sustainability -->
        <template #cell(Sustainability)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'Sustainability')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'Sustainability'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Institutions & Governance -->
        <template #cell(InstitutionsGovernance)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'InstitutionsGovernance')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'InstitutionsGovernance'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Transport & Infrastructure -->
        <template #cell(TransportInfrastructure)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'TransportInfrastructure')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'TransportInfrastructure'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Innovation -->
        <template #cell(Innovation)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'Innovation')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'Innovation'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Vibrancy -->
        <template #cell(Vibrancy)="data">
          <div class="py-3" :class="getBackgroundClass(data.value, 'Vibrancy')">
            <transition name="fade" mode="out-in">
              <span :key="mode + 'Vibrancy'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- E-Readiness -->
        <template #cell(EIntelligence)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'EIntelligence')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'EIntelligence'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Sub-pillar Templates -->
        <!-- Liveability -->
        <template #cell(Liveability)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'Liveability')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'Liveability'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Access to Services -->
        <template #cell(AccessToServices)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'AccessToServices')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'AccessToServices'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Investment Openness -->
        <template #cell(InvestmentOpenness)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'InvestmentOpenness')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'InvestmentOpenness'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Talent Competitiveness -->
        <template #cell(TalentCompetitiveness)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'TalentCompetitiveness')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'TalentCompetitiveness'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Urban Design -->
        <template #cell(UrbanDesign)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'UrbanDesign')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'UrbanDesign'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Energy and Waste -->
        <template #cell(EnergyWaste)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'EnergyWaste')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'EnergyWaste'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Institutions -->
        <template #cell(Institutions)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'Institutions')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'Institutions'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Governance -->
        <template #cell(Governance)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'Governance')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'Governance'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Mobility -->
        <template #cell(Mobility)="data">
          <div class="py-3" :class="getBackgroundClass(data.value, 'Mobility')">
            <transition name="fade" mode="out-in">
              <span :key="mode + 'Mobility'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Digital Infrastructure -->
        <template #cell(DigitalInfrastructure)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'DigitalInfrastructure')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'DigitalInfrastructure'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- R&D Intensity -->
        <template #cell(RDIntensity)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'RDIntensity')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'RDIntensity'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Intellectual Property Intensity -->
        <template #cell(IPIntensity)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'IPIntensity')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'IPIntensity'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Financial Activity -->
        <template #cell(FinancialActivity)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'FinancialActivity')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'FinancialActivity'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Cultural Vibrancy -->
        <template #cell(CulturalVibrancy)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'CulturalVibrancy')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'CulturalVibrancy'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- Data Readiness -->
        <template #cell(DataReadiness)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'DataReadiness')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'DataReadiness'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>

        <!-- AI Readiness -->
        <template #cell(AIReadiness)="data">
          <div
            class="py-3"
            :class="getBackgroundClass(data.value, 'AIReadiness')"
          >
            <transition name="fade" mode="out-in">
              <span :key="mode + 'AIReadiness'">
                {{
                  mode === "score"
                    ? data.value.score.toFixed(2)
                    : data.value.rank
                }}
              </span>
            </transition>
          </div>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import { BTable } from "bootstrap-vue";
import cityRankingData from "../../data_2025/rankings-table/cityRankings.js";

export default {
  name: "RankingsTable",
  components: {
    BTable,
  },
  data() {
    return {
      transProps: {
        name: "flip-list",
      },
      mode: "rank",
      cityRankingData: cityRankingData,

      modeOptions: [
        { text: "Show pillar ranks", value: "rank" },
        { text: "Show pillar scores", value: "score" },
      ],

      mainPillars: [
        {
          key: "PeopleCentricity",
          field: "people_centricity",
          label: "People Centricity",
          visible: true,
        },
        {
          key: "Openness",
          field: "openness",
          label: "Openness",
          visible: true,
        },
        {
          key: "Sustainability",
          field: "sustainability",
          label: "Sustainability",
          visible: true,
        },
        {
          key: "InstitutionsGovernance",
          field: "institutions_governance",
          label: "Institutions & Governance",
          visible: true,
        },
        {
          key: "TransportInfrastructure",
          field: "transport_infrastructure",
          label: "Transport & Infrastructure",
          visible: true,
        },
        {
          key: "Innovation",
          field: "innovation",
          label: "Innovation",
          visible: true,
        },
        {
          key: "Vibrancy",
          field: "vibrancy",
          label: "Vibrancy",
          visible: true,
        },
        {
          key: "EIntelligence",
          field: "e_intelligence",
          label: "E-Readiness",
          visible: true,
        },
      ],

      subPillars: [
        {
          key: "Liveability",
          field: "liveability",
          label: "Liveability",
          visible: false,
        },
        {
          key: "AccessToServices",
          field: "access_to_services",
          label: "Access to Services",
          visible: false,
        },
        {
          key: "InvestmentOpenness",
          field: "investment_openness",
          label: "Investment Openness",
          visible: false,
        },
        {
          key: "TalentCompetitiveness",
          field: "talent_competitiveness",
          label: "Talent Competitiveness",
          visible: false,
        },
        {
          key: "UrbanDesign",
          field: "urban_design",
          label: "Urban Design",
          visible: false,
        },
        {
          key: "EnergyWaste",
          field: "energy_waste",
          label: "Energy and Waste",
          visible: false,
        },
        {
          key: "Institutions",
          field: "institutions",
          label: "Institutions",
          visible: false,
        },
        {
          key: "Governance",
          field: "governance",
          label: "Governance",
          visible: false,
        },
        {
          key: "Mobility",
          field: "mobility",
          label: "Mobility",
          visible: false,
        },
        {
          key: "DigitalInfrastructure",
          field: "digital_infrastructure",
          label: "Digital Infrastructure",
          visible: false,
        },
        {
          key: "RDIntensity",
          field: "rd_intensity",
          label: "R&D Intensity",
          visible: false,
        },
        {
          key: "IPIntensity",
          field: "ip_intensity",
          label: "Intellectual Property Intensity",
          visible: false,
        },
        {
          key: "FinancialActivity",
          field: "financial_activity",
          label: "Financial Activity",
          visible: false,
        },
        {
          key: "CulturalVibrancy",
          field: "cultural_vibrancy",
          label: "Cultural Vibrancy",
          visible: false,
        },
        {
          key: "DataReadiness",
          field: "data_readiness",
          label: "Data Readiness",
          visible: false,
        },
        {
          key: "AIReadiness",
          field: "ai_readiness",
          label: "AI Readiness",
          visible: false,
        },
      ],
    };
  },

  computed: {
    allColumns() {
      return [...this.mainPillars, ...this.subPillars];
    },

    visibleFields() {
      const baseFields = [
        {
          key: "Rank",
          sortable: false,
          tdClass: "city-rank-column",
          thClass: "comparisonHeader text-white",
          sortDirection: "desc",
        },
        {
          key: "City",
          sortable: false,
          tdClass: "city-name-column",
          thClass: "comparisonHeader",
          sortDirection: "desc",
        },
      ];

      const futureReadinessField = {
        key: "FutureReadiness",
        label: "CFRI",
        sortable: false,
        thClass: "comparisonHeader bg-primary text-white",
        tdClass: "text-center comparisonCellNoPadding",
        sortDirection: "desc",
      };

      const visibleColumns = this.allColumns
        .filter((col) => col.visible)
        .map((col) => ({
          key: col.key,
          label: col.label,
          sortable: false,
          thClass: "comparisonHeader",
          tdClass:
            "text-center comparisonCellNoPadding pillar-column-cell-color",
          sortDirection: "desc",
        }));

      return [...baseFields, futureReadinessField, ...visibleColumns];
    },

    sortedCities() {
      return this.cityRankingData
        .slice()
        .sort((a, b) => a.future_readiness.rank - b.future_readiness.rank)
        .map((city) => {
          const mappedCity = {
            City: city.City,
            Country: city.Country,
            Region: city.Region,
            Slug: city.Slug,
            FutureReadiness: city.future_readiness,
          };

          this.allColumns.forEach((col) => {
            mappedCity[col.key] = city[col.field];
          });

          return mappedCity;
        });
    },

    isMobile() {
      return screen.width <= 760;
    },
  },

  methods: {
    goToProfile(citySlug) {
      const query = { ...this.$route.query, page: "profiles", city: citySlug };
      this.$router.replace({ query });
      window.location.reload();
    },

    getBackgroundClass(value) {
      if (!value || !value.rank) return "";

      const maxRank = 102;
      const opacityValue = (maxRank - value.rank) / maxRank;
      const opacityLevel = Math.max(
        1,
        Math.min(10, Math.round(opacityValue * 10))
      );
      return `comparisonCellNoPadding-${opacityLevel}`;
    },

    showAllPillars() {
      this.mainPillars.forEach((pillar) => (pillar.visible = true));
    },

    hideAllColumns() {
      this.mainPillars.forEach((pillar) => (pillar.visible = false));
      this.subPillars.forEach((subPillar) => (subPillar.visible = false));
    },

    showDefaultColumns() {
      // Show main pillars by default, hide sub-pillars
      this.mainPillars.forEach((pillar) => (pillar.visible = true));
      this.subPillars.forEach((subPillar) => (subPillar.visible = false));

      // Hide some less critical main pillars by default
      const defaultHidden = ["TransportInfrastructure", "Vibrancy"];
      this.mainPillars.forEach((pillar) => {
        if (defaultHidden.includes(pillar.key)) {
          pillar.visible = false;
        }
      });
    },
  },
};
</script>

<style>
.ranking-toggle {
  margin: auto;
  text-align: center;
}

.city-cell {
  text-align: left;
  min-width: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0.75rem 0;
  height: 100%;
}

.rank-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 0;
  height: 100%;
  text-align: center;
}
.rankingsTable td:nth-child(1),
.rankingsTable td:nth-child(2) {
  padding: 0 !important;
}

.city-name {
  font-weight: 600;
  color: #2c3e50;
  cursor: pointer;
  transition: color 0.2s ease;
}

.city-name:hover {
  color: #3498db;
  text-decoration: underline;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.rankingsTable {
  font-size: 0.875rem;
}

.rankingsTable th {
  border-top: solid 5px #fff !important;
  text-align: center;
  font-weight: 600;
  vertical-align: middle;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
}

/* Future Readiness column (3rd column) - special blue header styling */
.rankingsTable th:nth-child(3).bg-primary {
  background: #4285f4 !important;
  color: white !important;
}

.rankingsTable th:focus {
  outline: 0;
}
.city-rank-column {
  background-color: #f8f9fa;
  font-weight: bold;
  text-align: center;
  min-width: 60px;
}

.city-name-column {
  background-color: #f8f9fa;
  min-width: 150px;
  position: sticky;
  left: 0;
  z-index: 5;
}

/**
* 3rd column - Blue
*/
td:nth-child(3) .comparisonCellNoPadding-1 {
  background-color: rgba(66, 133, 244, 0.1);
}
td:nth-child(3) .comparisonCellNoPadding-2 {
  background-color: rgba(66, 133, 244, 0.2);
}
td:nth-child(3) .comparisonCellNoPadding-3 {
  background-color: rgba(66, 133, 244, 0.3);
}
td:nth-child(3) .comparisonCellNoPadding-4 {
  background-color: rgba(66, 133, 244, 0.4);
}
td:nth-child(3) .comparisonCellNoPadding-5 {
  background-color: rgba(66, 133, 244, 0.5);
}
td:nth-child(3) .comparisonCellNoPadding-6 {
  background-color: rgba(66, 133, 244, 0.6);
}
td:nth-child(3) .comparisonCellNoPadding-7 {
  background-color: rgba(66, 133, 244, 0.7);
}
td:nth-child(3) .comparisonCellNoPadding-8 {
  background-color: rgba(66, 133, 244, 0.8);
}
td:nth-child(3) .comparisonCellNoPadding-9 {
  background-color: rgba(66, 133, 244, 0.9);
}
td:nth-child(3) .comparisonCellNoPadding-10 {
  background-color: rgba(66, 133, 244, 1);
  color: white;
}

/**
* 4th column - Dark Grey
*/
td:nth-child(4) .comparisonCellNoPadding-1 {
  background-color: rgba(69, 69, 69, 0.1);
}
td:nth-child(4) .comparisonCellNoPadding-2 {
  background-color: rgba(69, 69, 69, 0.2);
}
td:nth-child(4) .comparisonCellNoPadding-3 {
  background-color: rgba(69, 69, 69, 0.3);
}
td:nth-child(4) .comparisonCellNoPadding-4 {
  background-color: rgba(69, 69, 69, 0.4);
}
td:nth-child(4) .comparisonCellNoPadding-5 {
  background-color: rgba(69, 69, 69, 0.5);
}
td:nth-child(4) .comparisonCellNoPadding-6 {
  background-color: rgba(69, 69, 69, 0.6);
}
td:nth-child(4) .comparisonCellNoPadding-7 {
  background-color: rgba(69, 69, 69, 0.7);
}
td:nth-child(4) .comparisonCellNoPadding-8 {
  background-color: rgba(69, 69, 69, 0.8);
}
td:nth-child(4) .comparisonCellNoPadding-9 {
  background-color: rgba(69, 69, 69, 0.9);
}
td:nth-child(4) .comparisonCellNoPadding-10 {
  background-color: rgba(69, 69, 69, 1);
  color: white;
}

/**
* 5th column - Yellow
*/
td:nth-child(5) .comparisonCellNoPadding-1 {
  background-color: rgba(244, 180, 0, 0.1);
}
td:nth-child(5) .comparisonCellNoPadding-2 {
  background-color: rgba(244, 180, 0, 0.2);
}
td:nth-child(5) .comparisonCellNoPadding-3 {
  background-color: rgba(244, 180, 0, 0.3);
}
td:nth-child(5) .comparisonCellNoPadding-4 {
  background-color: rgba(244, 180, 0, 0.4);
}
td:nth-child(5) .comparisonCellNoPadding-5 {
  background-color: rgba(244, 180, 0, 0.5);
}
td:nth-child(5) .comparisonCellNoPadding-6 {
  background-color: rgba(244, 180, 0, 0.6);
}
td:nth-child(5) .comparisonCellNoPadding-7 {
  background-color: rgba(244, 180, 0, 0.7);
}
td:nth-child(5) .comparisonCellNoPadding-8 {
  background-color: rgba(244, 180, 0, 0.8);
}
td:nth-child(5) .comparisonCellNoPadding-9 {
  background-color: rgba(244, 180, 0, 0.9);
}
td:nth-child(5) .comparisonCellNoPadding-10 {
  background-color: rgba(244, 180, 0, 1);
  color: white;
}

/**
* 6th column - Green
*/
td:nth-child(6) .comparisonCellNoPadding-1 {
  background-color: rgba(15, 157, 88, 0.1);
}
td:nth-child(6) .comparisonCellNoPadding-2 {
  background-color: rgba(15, 157, 88, 0.2);
}
td:nth-child(6) .comparisonCellNoPadding-3 {
  background-color: rgba(15, 157, 88, 0.3);
}
td:nth-child(6) .comparisonCellNoPadding-4 {
  background-color: rgba(15, 157, 88, 0.4);
}
td:nth-child(6) .comparisonCellNoPadding-5 {
  background-color: rgba(15, 157, 88, 0.5);
}
td:nth-child(6) .comparisonCellNoPadding-6 {
  background-color: rgba(15, 157, 88, 0.6);
}
td:nth-child(6) .comparisonCellNoPadding-7 {
  background-color: rgba(15, 157, 88, 0.7);
}
td:nth-child(6) .comparisonCellNoPadding-8 {
  background-color: rgba(15, 157, 88, 0.8);
}
td:nth-child(6) .comparisonCellNoPadding-9 {
  background-color: rgba(15, 157, 88, 0.9);
}
td:nth-child(6) .comparisonCellNoPadding-10 {
  background-color: rgba(15, 157, 88, 1);
  color: white;
}

/**
* 7th column - Purple
*/
td:nth-child(7) .comparisonCellNoPadding-1 {
  background-color: rgba(156, 39, 176, 0.1);
}
td:nth-child(7) .comparisonCellNoPadding-2 {
  background-color: rgba(156, 39, 176, 0.2);
}
td:nth-child(7) .comparisonCellNoPadding-3 {
  background-color: rgba(156, 39, 176, 0.3);
}
td:nth-child(7) .comparisonCellNoPadding-4 {
  background-color: rgba(156, 39, 176, 0.4);
}
td:nth-child(7) .comparisonCellNoPadding-5 {
  background-color: rgba(156, 39, 176, 0.5);
}
td:nth-child(7) .comparisonCellNoPadding-6 {
  background-color: rgba(156, 39, 176, 0.6);
}
td:nth-child(7) .comparisonCellNoPadding-7 {
  background-color: rgba(156, 39, 176, 0.7);
}
td:nth-child(7) .comparisonCellNoPadding-8 {
  background-color: rgba(156, 39, 176, 0.8);
}
td:nth-child(7) .comparisonCellNoPadding-9 {
  background-color: rgba(156, 39, 176, 0.9);
}
td:nth-child(7) .comparisonCellNoPadding-10 {
  background-color: rgba(156, 39, 176, 1);
  color: white;
}

/**
* 8th column - Teal 
*/
td:nth-child(8) .comparisonCellNoPadding-1 {
  background-color: rgba(0, 150, 136, 0.1);
}
td:nth-child(8) .comparisonCellNoPadding-2 {
  background-color: rgba(0, 150, 136, 0.2);
}
td:nth-child(8) .comparisonCellNoPadding-3 {
  background-color: rgba(0, 150, 136, 0.3);
}
td:nth-child(8) .comparisonCellNoPadding-4 {
  background-color: rgba(0, 150, 136, 0.4);
}
td:nth-child(8) .comparisonCellNoPadding-5 {
  background-color: rgba(0, 150, 136, 0.5);
}
td:nth-child(8) .comparisonCellNoPadding-6 {
  background-color: rgba(0, 150, 136, 0.6);
}
td:nth-child(8) .comparisonCellNoPadding-7 {
  background-color: rgba(0, 150, 136, 0.7);
}
td:nth-child(8) .comparisonCellNoPadding-8 {
  background-color: rgba(0, 150, 136, 0.8);
}
td:nth-child(8) .comparisonCellNoPadding-9 {
  background-color: rgba(0, 150, 136, 0.9);
}
td:nth-child(8) .comparisonCellNoPadding-10 {
  background-color: rgba(0, 150, 136, 1);
  color: white;
}

/**
* 9th column - Orange
*/
td:nth-child(9) .comparisonCellNoPadding-1 {
  background-color: rgba(255, 152, 0, 0.1);
}
td:nth-child(9) .comparisonCellNoPadding-2 {
  background-color: rgba(255, 152, 0, 0.2);
}
td:nth-child(9) .comparisonCellNoPadding-3 {
  background-color: rgba(255, 152, 0, 0.3);
}
td:nth-child(9) .comparisonCellNoPadding-4 {
  background-color: rgba(255, 152, 0, 0.4);
}
td:nth-child(9) .comparisonCellNoPadding-5 {
  background-color: rgba(255, 152, 0, 0.5);
}
td:nth-child(9) .comparisonCellNoPadding-6 {
  background-color: rgba(255, 152, 0, 0.6);
}
td:nth-child(9) .comparisonCellNoPadding-7 {
  background-color: rgba(255, 152, 0, 0.7);
}
td:nth-child(9) .comparisonCellNoPadding-8 {
  background-color: rgba(255, 152, 0, 0.8);
}
td:nth-child(9) .comparisonCellNoPadding-9 {
  background-color: rgba(255, 152, 0, 0.9);
}
td:nth-child(9) .comparisonCellNoPadding-10 {
  background-color: rgba(255, 152, 0, 1);
  color: white;
}

/**
* 10th column - Dark Grey
*/
td:nth-child(10) .comparisonCellNoPadding-1 {
  background-color: rgba(69, 69, 69, 0.1);
}
td:nth-child(10) .comparisonCellNoPadding-2 {
  background-color: rgba(69, 69, 69, 0.2);
}
td:nth-child(10) .comparisonCellNoPadding-3 {
  background-color: rgba(69, 69, 69, 0.3);
}
td:nth-child(10) .comparisonCellNoPadding-4 {
  background-color: rgba(69, 69, 69, 0.4);
}
td:nth-child(10) .comparisonCellNoPadding-5 {
  background-color: rgba(69, 69, 69, 0.5);
}
td:nth-child(10) .comparisonCellNoPadding-6 {
  background-color: rgba(69, 69, 69, 0.6);
}
td:nth-child(10) .comparisonCellNoPadding-7 {
  background-color: rgba(69, 69, 69, 0.7);
}
td:nth-child(10) .comparisonCellNoPadding-8 {
  background-color: rgba(69, 69, 69, 0.8);
}
td:nth-child(10) .comparisonCellNoPadding-9 {
  background-color: rgba(69, 69, 69, 0.9);
}
td:nth-child(10) .comparisonCellNoPadding-10 {
  background-color: rgba(69, 69, 69, 1);
  color: white;
}

/**
* 11th column - Green
*/
td:nth-child(11) .comparisonCellNoPadding-1 {
  background-color: rgba(0, 150, 136, 0.1);
}
td:nth-child(11) .comparisonCellNoPadding-2 {
  background-color: rgba(0, 150, 136, 0.2);
}
td:nth-child(11) .comparisonCellNoPadding-3 {
  background-color: rgba(0, 150, 136, 0.3);
}
td:nth-child(11) .comparisonCellNoPadding-4 {
  background-color: rgba(0, 150, 136, 0.4);
}
td:nth-child(11) .comparisonCellNoPadding-5 {
  background-color: rgba(0, 150, 136, 0.5);
}
td:nth-child(11) .comparisonCellNoPadding-6 {
  background-color: rgba(0, 150, 136, 0.6);
}
td:nth-child(11) .comparisonCellNoPadding-7 {
  background-color: rgba(0, 150, 136, 0.7);
}
td:nth-child(11) .comparisonCellNoPadding-8 {
  background-color: rgba(0, 150, 136, 0.8);
}
td:nth-child(11) .comparisonCellNoPadding-9 {
  background-color: rgba(0, 150, 136, 0.9);
}
td:nth-child(11) .comparisonCellNoPadding-10 {
  background-color: rgba(0, 150, 136, 1);
  color: white;
}

/**
* For columns beyond 11th, cycle through colors using modulo pattern
* This ensures a visually distinct pattern for additional pillars
* 12th+ columns will use a cycling pattern of the above color
*/
td:nth-child(n + 12) .comparisonCellNoPadding-1 {
  background-color: rgba(0, 150, 136, 0.1);
}
td:nth-child(n + 12) .comparisonCellNoPadding-2 {
  background-color: rgba(0, 150, 136, 0.2);
}
td:nth-child(n + 12) .comparisonCellNoPadding-3 {
  background-color: rgba(0, 150, 136, 0.3);
}
td:nth-child(n + 12) .comparisonCellNoPadding-4 {
  background-color: rgba(0, 150, 136, 0.4);
}
td:nth-child(n + 12) .comparisonCellNoPadding-5 {
  background-color: rgba(0, 150, 136, 0.5);
}
td:nth-child(n + 12) .comparisonCellNoPadding-6 {
  background-color: rgba(0, 150, 136, 0.6);
}
td:nth-child(n + 12) .comparisonCellNoPadding-7 {
  background-color: rgba(0, 150, 136, 0.7);
}
td:nth-child(n + 12) .comparisonCellNoPadding-8 {
  background-color: rgba(0, 150, 136, 0.8);
}
td:nth-child(n + 12) .comparisonCellNoPadding-9 {
  background-color: rgba(0, 150, 136, 0.9);
}
td:nth-child(n + 12) .comparisonCellNoPadding-10 {
  background-color: rgba(0, 150, 136, 1);
  color: white;
}

/**
* Alternating colors for additional columns
*/
td:nth-child(4n + 12) .comparisonCellNoPadding-1 {
  background-color: rgba(0, 150, 136, 0.1);
}
td:nth-child(4n + 12) .comparisonCellNoPadding-2 {
  background-color: rgba(0, 150, 136, 0.2);
}
td:nth-child(4n + 12) .comparisonCellNoPadding-3 {
  background-color: rgba(0, 150, 136, 0.3);
}
td:nth-child(4n + 12) .comparisonCellNoPadding-4 {
  background-color: rgba(0, 150, 136, 0.4);
}
td:nth-child(4n + 12) .comparisonCellNoPadding-5 {
  background-color: rgba(0, 150, 136, 0.5);
}
td:nth-child(4n + 12) .comparisonCellNoPadding-6 {
  background-color: rgba(0, 150, 136, 0.6);
}
td:nth-child(4n + 12) .comparisonCellNoPadding-7 {
  background-color: rgba(0, 150, 136, 0.7);
}
td:nth-child(4n + 12) .comparisonCellNoPadding-8 {
  background-color: rgba(0, 150, 136, 0.8);
}
td:nth-child(4n + 12) .comparisonCellNoPadding-9 {
  background-color: rgba(0, 150, 136, 0.9);
}
td:nth-child(4n + 12) .comparisonCellNoPadding-10 {
  background-color: rgba(0, 150, 136, 1);
  color: white;
}
</style>
